/**
 * BrandingSettings Component
 * 
 * SaaS Admin Panel - Branding Customization
 * 
 * Version: 6.2.0
 */
import React, { useState, useRef } from 'react'
import './BrandingSettings.css'

interface BrandConfig {
  logoUrl: string
  faviconUrl: string
  brandName: string
  primaryColor: string
  secondaryColor: string
  accentColor: string
  tagline: string
  copyrightText: string
}

interface BrandingSettingsProps {
  initialConfig?: Partial<BrandConfig>
  onSave?: (config: BrandConfig) => void
  onPreview?: (config: BrandConfig) => void
  className?: string
}

const defaultConfig: BrandConfig = {
  logoUrl: '',
  faviconUrl: '',
  brandName: 'ZO Bull',
  primaryColor: '#2962ff',
  secondaryColor: '#00c853',
  accentColor: '#ff9100',
  tagline: 'ZO Bull Algo Trading Platform',
  copyrightText: '© 2026 ZO Bull. All rights reserved.'
}

const presetColors = [
  { name: 'TradingView Blue', color: '#2962ff' },
  { name: 'Binance Yellow', color: '#f0b90b' },
  { name: 'Success Green', color: '#00c853' },
  { name: 'Warning Orange', color: '#ff9100' },
  { name: 'Danger Red', color: '#ff1744' },
  { name: 'Purple', color: '#9c27b0' },
  { name: 'Teal', color: '#00bcd4' },
  { name: 'Pink', color: '#e91e63' },
]

const BrandingSettings: React.FC<BrandingSettingsProps> = ({
  initialConfig,
  onSave,
  onPreview,
  className = ''
}) => {
  const [config, setConfig] = useState<BrandConfig>({ ...defaultConfig, ...initialConfig })
  const [activeSection, setActiveSection] = useState<'logo' | 'colors' | 'text' | 'preview'>('logo')
  const [hasChanges, setHasChanges] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)
  const faviconInputRef = useRef<HTMLInputElement>(null)

  const updateConfig = (key: keyof BrandConfig, value: string) => {
    setConfig(prev => ({ ...prev, [key]: value }))
    setHasChanges(true)
    onPreview?.({ ...config, [key]: value })
  }

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'favicon') => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        const url = reader.result as string
        updateConfig(type === 'logo' ? 'logoUrl' : 'faviconUrl', url)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSave = () => {
    onSave?.(config)
    setHasChanges(false)
  }

  const handleReset = () => {
    setConfig(defaultConfig)
    setHasChanges(true)
  }

  const handlePresetColor = (color: string, type: 'primary' | 'secondary' | 'accent') => {
    const key = type === 'primary' ? 'primaryColor' : type === 'secondary' ? 'secondaryColor' : 'accentColor'
    updateConfig(key, color)
  }

  const sections = [
    { id: 'logo', label: 'Logo & Icons', icon: '🖼️' },
    { id: 'colors', label: 'Brand Colors', icon: '🎨' },
    { id: 'text', label: 'Text & Content', icon: '📝' },
    { id: 'preview', label: 'Live Preview', icon: '👁️' }
  ]

  return (
    <div className={`branding-settings ${className}`}>
      {/* Section Tabs */}
      <div className="branding-tabs">
        {sections.map(section => (
          <button
            key={section.id}
            className={`branding-tab ${activeSection === section.id ? 'active' : ''}`}
            onClick={() => setActiveSection(section.id as typeof activeSection)}
          >
            <span className="tab-icon">{section.icon}</span>
            <span className="tab-label">{section.label}</span>
          </button>
        ))}
      </div>

      {/* Section Content */}
      <div className="branding-content">
        {/* Logo & Icons Section */}
        {activeSection === 'logo' && (
          <div className="section-panel">
            <h3 className="section-title">Logo & Icons</h3>
            <p className="section-description">
              Upload your brand logo and favicon. Recommended: PNG with transparent background.
            </p>

            <div className="logo-upload-grid">
              {/* Main Logo */}
              <div className="logo-upload-card">
                <label className="upload-label">Main Logo</label>
                <div 
                  className="logo-preview-box"
                  onClick={() => logoInputRef.current?.click()}
                >
                  {config.logoUrl ? (
                    <img src={config.logoUrl} alt="Logo" className="preview-image" />
                  ) : (
                    <div className="upload-placeholder">
                      <span className="placeholder-icon">📷</span>
                      <span className="placeholder-text">Click to upload logo</span>
                      <span className="placeholder-hint">PNG, JPG up to 2MB</span>
                    </div>
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleLogoUpload(e, 'logo')}
                  className="hidden-input"
                />
                {config.logoUrl && (
                  <button 
                    className="remove-btn"
                    onClick={() => updateConfig('logoUrl', '')}
                  >
                    Remove Logo
                  </button>
                )}
              </div>

              {/* Favicon */}
              <div className="logo-upload-card">
                <label className="upload-label">Favicon (32x32)</label>
                <div 
                  className="logo-preview-box small"
                  onClick={() => faviconInputRef.current?.click()}
                >
                  {config.faviconUrl ? (
                    <img src={config.faviconUrl} alt="Favicon" className="preview-image" />
                  ) : (
                    <div className="upload-placeholder small">
                      <span className="placeholder-icon">🔲</span>
                      <span className="placeholder-text">Favicon</span>
                    </div>
                  )}
                </div>
                <input
                  ref={faviconInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleLogoUpload(e, 'favicon')}
                  className="hidden-input"
                />
                {config.faviconUrl && (
                  <button 
                    className="remove-btn"
                    onClick={() => updateConfig('faviconUrl', '')}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            <div className="logo-presets">
              <label className="presets-label">Quick Presets</label>
              <div className="preset-buttons">
                <button className="preset-btn" onClick={() => {
                  updateConfig('logoUrl', '')
                  updateConfig('brandName', 'ZO Bull')
                }}>
                  🎯 ZO Bull Default
                </button>
                <button className="preset-btn" onClick={() => {
                  updateConfig('logoUrl', '')
                  updateConfig('brandName', 'TradeBot')
                }}>
                  🤖 TradeBot
                </button>
                <button className="preset-btn" onClick={() => {
                  updateConfig('logoUrl', '')
                  updateConfig('brandName', 'AlgoPro')
                }}>
                  📈 AlgoPro
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Colors Section */}
        {activeSection === 'colors' && (
          <div className="section-panel">
            <h3 className="section-title">Brand Colors</h3>
            <p className="section-description">
              Customize your brand colors. These will be used throughout the platform.
            </p>

            <div className="color-pickers">
              {/* Primary Color */}
              <div className="color-picker-item">
                <label className="color-label">Primary Color</label>
                <div className="color-input-group">
                  <input
                    type="color"
                    value={config.primaryColor}
                    onChange={(e) => updateConfig('primaryColor', e.target.value)}
                    className="color-input"
                  />
                  <input
                    type="text"
                    value={config.primaryColor}
                    onChange={(e) => updateConfig('primaryColor', e.target.value)}
                    className="color-text-input"
                    placeholder="#2962ff"
                  />
                </div>
                <div className="color-presets">
                  {presetColors.map(preset => (
                    <button
                      key={preset.name}
                      className={`color-preset ${config.primaryColor === preset.color ? 'active' : ''}`}
                      style={{ backgroundColor: preset.color }}
                      onClick={() => handlePresetColor(preset.color, 'primary')}
                      title={preset.name}
                    />
                  ))}
                </div>
              </div>

              {/* Secondary Color */}
              <div className="color-picker-item">
                <label className="color-label">Secondary Color</label>
                <div className="color-input-group">
                  <input
                    type="color"
                    value={config.secondaryColor}
                    onChange={(e) => updateConfig('secondaryColor', e.target.value)}
                    className="color-input"
                  />
                  <input
                    type="text"
                    value={config.secondaryColor}
                    onChange={(e) => updateConfig('secondaryColor', e.target.value)}
                    className="color-text-input"
                    placeholder="#00c853"
                  />
                </div>
              </div>

              {/* Accent Color */}
              <div className="color-picker-item">
                <label className="color-label">Accent Color</label>
                <div className="color-input-group">
                  <input
                    type="color"
                    value={config.accentColor}
                    onChange={(e) => updateConfig('accentColor', e.target.value)}
                    className="color-input"
                  />
                  <input
                    type="text"
                    value={config.accentColor}
                    onChange={(e) => updateConfig('accentColor', e.target.value)}
                    className="color-text-input"
                    placeholder="#ff9100"
                  />
                </div>
              </div>
            </div>

            {/* Color Preview */}
            <div className="color-preview-section">
              <label className="preview-label">Color Preview</label>
              <div className="color-preview-grid">
                <div 
                  className="preview-swatch primary"
                  style={{ backgroundColor: config.primaryColor }}
                >
                  Primary
                </div>
                <div 
                  className="preview-swatch secondary"
                  style={{ backgroundColor: config.secondaryColor }}
                >
                  Secondary
                </div>
                <div 
                  className="preview-swatch accent"
                  style={{ backgroundColor: config.accentColor }}
                >
                  Accent
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Text & Content Section */}
        {activeSection === 'text' && (
          <div className="section-panel">
            <h3 className="section-title">Text & Content</h3>
            <p className="section-description">
              Customize your brand text, tagline, and copyright information.
            </p>

            <div className="text-inputs">
              <div className="text-input-group">
                <label className="input-label">Brand Name</label>
                <input
                  type="text"
                  value={config.brandName}
                  onChange={(e) => updateConfig('brandName', e.target.value)}
                  className="text-input"
                  placeholder="Your Brand Name"
                  maxLength={50}
                />
                <span className="input-hint">{config.brandName.length}/50 characters</span>
              </div>

              <div className="text-input-group">
                <label className="input-label">Tagline</label>
                <input
                  type="text"
                  value={config.tagline}
                  onChange={(e) => updateConfig('tagline', e.target.value)}
                  className="text-input"
                  placeholder="Your catchy tagline"
                  maxLength={100}
                />
                <span className="input-hint">{config.tagline.length}/100 characters</span>
              </div>

              <div className="text-input-group">
                <label className="input-label">Copyright Text</label>
                <input
                  type="text"
                  value={config.copyrightText}
                  onChange={(e) => updateConfig('copyrightText', e.target.value)}
                  className="text-input"
                  placeholder="© 2026 Your Company. All rights reserved."
                  maxLength={150}
                />
                <span className="input-hint">{config.copyrightText.length}/150 characters</span>
              </div>
            </div>
          </div>
        )}

        {/* Live Preview Section */}
        {activeSection === 'preview' && (
          <div className="section-panel">
            <h3 className="section-title">Live Preview</h3>
            <p className="section-description">
              See how your branding will look on the platform.
            </p>

            <div className="preview-container">
              {/* Header Preview */}
              <div 
                className="preview-header"
                style={{ backgroundColor: config.primaryColor }}
              >
                <div className="preview-logo">
                  {config.logoUrl ? (
                    <img src={config.logoUrl} alt="Logo" className="preview-logo-img" />
                  ) : (
                    <span className="preview-brand-name">{config.brandName}</span>
                  )}
                </div>
                <span className="preview-tagline">{config.tagline}</span>
              </div>

              {/* Content Preview */}
              <div className="preview-content">
                <div className="preview-card" style={{ borderColor: config.primaryColor }}>
                  <div 
                    className="preview-card-header"
                    style={{ backgroundColor: config.primaryColor }}
                  >
                    Sample Card
                  </div>
                  <div className="preview-card-body">
                    <div 
                      className="preview-button primary"
                      style={{ backgroundColor: config.primaryColor }}
                    >
                      Primary Button
                    </div>
                    <div 
                      className="preview-button secondary"
                      style={{ borderColor: config.secondaryColor, color: config.secondaryColor }}
                    >
                      Secondary Button
                    </div>
                  </div>
                </div>

                <div className="preview-badges">
                  <span 
                    className="preview-badge"
                    style={{ backgroundColor: `${config.primaryColor}20`, color: config.primaryColor }}
                  >
                    Success Badge
                  </span>
                  <span 
                    className="preview-badge"
                    style={{ backgroundColor: `${config.accentColor}20`, color: config.accentColor }}
                  >
                    Warning Badge
                  </span>
                </div>
              </div>

              {/* Footer Preview */}
              <div className="preview-footer">
                {config.copyrightText}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Bar */}
      <div className="branding-actions">
        <button className="action-btn reset" onClick={handleReset}>
          Reset to Default
        </button>
        <div className="action-right">
          {hasChanges && (
            <span className="unsaved-indicator">● Unsaved changes</span>
          )}
          <button 
            className="action-btn save" 
            onClick={handleSave}
            disabled={!hasChanges}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  )
}

export default BrandingSettings